import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useSelect } from 'downshift'
import { useEffect, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  SelectWrap,
  SelectToggleButton,
  SelectMenuWrap,
  SelectMenu,
  SelectItem,
  SelectPlaceholder,
  SelectedItem
} from '../components'
import { itemToString } from '../utils'
import { PB40T, PB70T } from '../../colors'

const Item = styled(SelectItem)`
  &.selected {
    display: none;
  }
  &.highlighted {
    background-color: #0a6085;
  }
`
const getItemClassName = (item, currentIdx, highlightedIdx, selected) => {
  const classes = []

  if (currentIdx === highlightedIdx) {
    classes.push('highlighted')
  }

  if (item === selected) {
    classes.push('selected')
  }

  return classes.join(' ')
}
const Select = ({
  id,
  labelledBy,
  items,
  value,
  onBlur,
  onChange,
  size = 'medium',
  color = PB40T,
  width = 'auto',
  menuDirection = 'down',
  placeholder = 'Select an option...',
  isDisabled = false
}) => {
  const {
    isOpen,
    selectItem,
    selectedItem,
    highlightedIndex,
    getToggleButtonProps,
    getMenuProps,
    getItemProps
  } = useSelect({
    items,
    itemToString,
    toggleButtonId: id,
    labelId: labelledBy,
    initialSelectedItem: value,
    onSelectedItemChange: useCallback(
      changes => {
        if (onChange) {
          const evt = new Event('change', {
            bubbles: true,
            cancelable: false
          })

          Object.defineProperty(evt, 'target', {
            writeable: false,
            value: { value: changes.selectedItem }
          })

          onChange(evt, changes.type)
        }
      },
      [onChange]
    )
  })

  useEffect(() => {
    if (value && !selectedItem) {
      selectItem(value)
    }
  }, [value, selectItem, selectedItem])

  return (
    <SelectWrap width={width} menuDirection={menuDirection}>
      <SelectToggleButton
        {...getToggleButtonProps({ onBlur })}
        type="button"
        data-select-toggle="true"
        size={size}
        color={color}
        disabled={isDisabled}
        menuDirection={menuDirection}>
        {selectedItem ? (
          <SelectedItem>{itemToString(selectedItem)}</SelectedItem>
        ) : (
          <SelectPlaceholder>{placeholder}</SelectPlaceholder>
        )}
        <FontAwesomeIcon icon="chevron-down" color={isOpen ? PB70T : PB40T} />
      </SelectToggleButton>
      <SelectMenuWrap menuDirection={menuDirection} width={width}>
        <SelectMenu
          data-select-menu="true"
          menuDirection={menuDirection}
          size={size}
          {...getMenuProps()}>
          {isOpen &&
            items.map((item, index) => (
              <Item
                {...getItemProps({ item, index })}
                key={index}
                size={size}
                color={color}
                className={getItemClassName(item, index, highlightedIndex, selectedItem)}>
                {itemToString(item)}
              </Item>
            ))}
        </SelectMenu>
      </SelectMenuWrap>
    </SelectWrap>
  )
}

Select.propTypes = {
  /** The items in the Select as options */
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string
      })
    ])
  ),
  /** The item that should be selected */
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ]),
  /** The callback handler when the selected item changes */
  onChange: PropTypes.func,
  /** The callback handler when the select loses focus */
  onBlur: PropTypes.func,
  /** Whether the Select is disabled */
  isDisabled: PropTypes.bool,
  /** The width of the Select */
  width: PropTypes.string,
  /** The color of the items text */
  color: PropTypes.string,
  /** The relative size of the Select when closed */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** The direction of the menu when expanded */
  menuDirection: PropTypes.oneOf(['up', 'down']),
  /** The default text when no item is selected */
  placeholder: PropTypes.string,
  /** The id of the Select, useful for accessibility */
  id: PropTypes.string,
  /** The id of the associated label (i.e. `Field` component), useful for accessibility */
  labelledBy: PropTypes.string
}

export { Select }
